import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { UserPlus, Check, MapPin, Phone, Mail, FileText } from "lucide-react";

const clientSchema = z.object({
  name_ar: z.string().min(2, "الاسم بالعربية مطلوب"),
  name_en: z.string().optional(),
  mobile: z.string().min(10, "رقم الجوال مطلوب"),
  email: z.string().email("البريد الإلكتروني غير صحيح").optional().or(z.literal("")),
  passport_number: z.string().optional(),
  passport_expiry: z.string().optional(),
  nationality: z.string().min(1, "الجنسية مطلوبة"),
  address: z.string().optional(),
  additional_info: z.string().optional(),
  preferred_contact_method: z.enum(["phone", "email", "whatsapp"]),
});

type ClientFormData = z.infer<typeof clientSchema>;

export default function ClientSelfRegistration() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const { toast } = useToast();

  const form = useForm<ClientFormData>({
    resolver: zodResolver(clientSchema),
    defaultValues: {
      name_ar: "",
      name_en: "",
      mobile: "",
      email: "",
      passport_number: "",
      passport_expiry: "",
      nationality: "",
      address: "",
      additional_info: "",
      preferred_contact_method: "phone",
    },
  });

  const onSubmit = async (data: ClientFormData) => {
    setIsSubmitting(true);
    try {
      // تحضير بيانات جواز السفر
      const passportInfo = {
        number: data.passport_number || "",
        expiry_date: data.passport_expiry || "",
        nationality: data.nationality,
      };

      // إضافة معلومات إضافية
      const additionalInfo = `
العنوان: ${data.address || "غير محدد"}
طريقة التواصل المفضلة: ${data.preferred_contact_method === "phone" ? "هاتف" : data.preferred_contact_method === "email" ? "بريد إلكتروني" : "واتساب"}
ملاحظات إضافية: ${data.additional_info || "لا توجد"}
تاريخ التسجيل الذاتي: ${new Date().toLocaleString('ar-SA')}
      `.trim();

      const { error } = await supabase.from("clients").insert({
        name_ar: data.name_ar,
        name_en: data.name_en || null,
        mobile: data.mobile,
        additional_info: additionalInfo,
        passport_info: passportInfo,
        status: "waiting", // حالة انتظار للمراجعة
      });

      if (error) throw error;

      setIsSubmitted(true);
      toast({
        title: "تم التسجيل بنجاح",
        description: "تم إرسال بياناتك بنجاح. سيتم التواصل معك قريباً.",
      });
    } catch (error: any) {
      console.error("Error submitting form:", error);
      toast({
        title: "خطأ في التسجيل",
        description: error.message || "حدث خطأ أثناء إرسال البيانات",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-secondary/5 flex items-center justify-center p-4">
        <Card className="w-full max-w-md text-center">
          <CardHeader>
            <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <Check className="w-8 h-8 text-green-600" />
            </div>
            <CardTitle className="text-2xl text-green-700">تم التسجيل بنجاح!</CardTitle>
            <CardDescription className="text-lg">
              شكراً لك على التسجيل. سيتم مراجعة بياناتك والتواصل معك قريباً.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button 
              onClick={() => {
                setIsSubmitted(false);
                form.reset();
              }}
              variant="outline"
              className="w-full"
            >
              تسجيل عميل آخر
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-secondary/5">
      <div className="container mx-auto py-8 px-4">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 rounded-full mb-4">
            <UserPlus className="w-8 h-8 text-primary" />
          </div>
          <h1 className="text-3xl font-bold text-foreground mb-2">تسجيل عميل جديد</h1>
          <p className="text-muted-foreground text-lg">
            يرجى ملء البيانات التالية للتسجيل في نظامنا
          </p>
        </div>

        <div className="max-w-2xl mx-auto">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                نموذج التسجيل
              </CardTitle>
              <CardDescription>
                جميع الحقول المطلوبة مُشار إليها بعلامة *
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                  {/* معلومات شخصية */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold border-b pb-2">المعلومات الشخصية</h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name="name_ar"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>الاسم بالعربية *</FormLabel>
                            <FormControl>
                              <Input placeholder="أدخل اسمك بالعربية" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="name_en"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>الاسم بالإنجليزية</FormLabel>
                            <FormControl>
                              <Input placeholder="Enter your name in English" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name="mobile"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="flex items-center gap-2">
                              <Phone className="w-4 h-4" />
                              رقم الجوال *
                            </FormLabel>
                            <FormControl>
                              <Input placeholder="05xxxxxxxx" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="email"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="flex items-center gap-2">
                              <Mail className="w-4 h-4" />
                              البريد الإلكتروني
                            </FormLabel>
                            <FormControl>
                              <Input placeholder="example@email.com" type="email" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <FormField
                      control={form.control}
                      name="nationality"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>الجنسية *</FormLabel>
                          <Select onValueChange={field.onChange} defaultValue={field.value}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="اختر الجنسية" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="سعودي">سعودي</SelectItem>
                              <SelectItem value="مصري">مصري</SelectItem>
                              <SelectItem value="أردني">أردني</SelectItem>
                              <SelectItem value="سوري">سوري</SelectItem>
                              <SelectItem value="لبناني">لبناني</SelectItem>
                              <SelectItem value="فلسطيني">فلسطيني</SelectItem>
                              <SelectItem value="يمني">يمني</SelectItem>
                              <SelectItem value="عراقي">عراقي</SelectItem>
                              <SelectItem value="أخرى">أخرى</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  {/* معلومات جواز السفر */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold border-b pb-2">معلومات جواز السفر</h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name="passport_number"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>رقم جواز السفر</FormLabel>
                            <FormControl>
                              <Input placeholder="رقم جواز السفر" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="passport_expiry"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>تاريخ انتهاء جواز السفر</FormLabel>
                            <FormControl>
                              <Input type="date" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>

                  {/* معلومات الاتصال */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold border-b pb-2">معلومات إضافية</h3>
                    
                    <FormField
                      control={form.control}
                      name="address"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="flex items-center gap-2">
                            <MapPin className="w-4 h-4" />
                            العنوان
                          </FormLabel>
                          <FormControl>
                            <Input placeholder="أدخل عنوانك" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="preferred_contact_method"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>طريقة التواصل المفضلة</FormLabel>
                          <Select onValueChange={field.onChange} defaultValue={field.value}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="phone">مكالمة هاتفية</SelectItem>
                              <SelectItem value="whatsapp">واتساب</SelectItem>
                              <SelectItem value="email">بريد إلكتروني</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="additional_info"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>ملاحظات إضافية</FormLabel>
                          <FormControl>
                            <Textarea 
                              placeholder="أي معلومات إضافية تود إضافتها"
                              className="min-h-[100px]"
                              {...field} 
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <Button type="submit" className="w-full" disabled={isSubmitting}>
                    {isSubmitting ? "جاري الإرسال..." : "إرسال البيانات"}
                  </Button>
                </form>
              </Form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}